import React from "react";
import { Truck, CalendarClock } from "lucide-react";
import { money } from "../../lib/api";
import { computeProductPrice, freeShippingState, shippingCents } from "../../lib/pricing";
import { useT } from "../../lib/i18n";

// Shows the shipping cost (from Business Settings) and a clearly-labelled,
// non-guaranteed production + transit estimate. Used after the address is valid.
export default function ShippingEstimate({ settings, draft }) {
  const { t } = useT();
  const pricing = computeProductPrice(settings, draft.kit_qty);
  const subtotal = pricing ? pricing.product_price_cents : 0;
  const fs = freeShippingState(settings, draft.kit_qty, subtotal);
  const isFree = !!(fs && fs.unlocked);
  const shipping = shippingCents(settings, draft.kit_qty, subtotal);
  const ful = settings?.fulfillment || {};
  const prod = Number(ful.production_time_days ?? 2);
  const tmin = Number(ful.transit_days_min ?? 3);
  const tmax = Number(ful.transit_days_max ?? 5);

  return (
    <div className="mt-5 rounded-xl border bg-white p-4" data-testid="shipping-estimate">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>
          <Truck className="h-4 w-4" style={{ color: "hsl(var(--brand-rose))" }} /> {t("shipping")}
        </span>
        {isFree ? (
          <span className="flex items-center gap-1 text-sm font-semibold" style={{ color: "hsl(var(--success))" }} data-testid="shipping-estimate-amount">
            {t("ship_free")} ✓
          </span>
        ) : (
          <span className="text-sm tabular-nums" data-testid="shipping-estimate-amount">{t("ship_standard")} — {money(shipping)}</span>
        )}
      </div>
      <div className="mt-3 flex gap-2 border-t pt-3">
        <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "hsl(var(--brand-rose))" }} />
        <div className="text-xs leading-relaxed text-[hsl(var(--muted-foreground))]">
          <div className="font-medium text-[hsl(var(--brand-brown))]">{t("delivery_title")}</div>
          <p>{t("delivery_prod", { n: prod })}</p>
          <p>{t("delivery_transit", { min: tmin, max: tmax })}</p>
          <p className="mt-1 italic">{t("delivery_estimate_note")}</p>
        </div>
      </div>
    </div>
  );
}
